import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserPlus, MessageCircle, CheckCircle2, XCircle, MailPlus, Phone } from "lucide-react";

const STATUS_META = {
  pending:   { label: "قيد الانتظار", color: "bg-amber-100 text-amber-800 border-amber-300" },
  contacted: { label: "تم التواصل",   color: "bg-blue-100 text-blue-800 border-blue-300" },
  approved:  { label: "معتمد",        color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  rejected:  { label: "مرفوض",        color: "bg-red-100 text-red-800 border-red-300" },
};

export default function AccountRequests() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteReq, setNoteReq] = useState(null);
  const [nextStatus, setNextStatus] = useState("contacted");
  const [note, setNote] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/account-requests", { params: filter === "all" ? {} : { status: filter } });
      setItems(data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, [filter]);

  const openAction = (r, status) => {
    setNoteReq(r); setNextStatus(status); setNote(r.admin_note || ""); setNoteOpen(true);
  };
  const doAction = async () => {
    try {
      await api.put(`/account-requests/${noteReq.id}`, { status: nextStatus, admin_note: note });
      toast.success("تم تحديث الحالة");
      setNoteOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const openWA = (phone, name) => {
    const digits = (phone || "").replace(/\D+/g, "");
    if (!digits) return toast.error("لا يوجد رقم واتساب");
    const text = encodeURIComponent(`مرحباً ${name}،\nشكراً لطلبك حساباً على محاسبتي. سأزودك ببيانات الدخول خلال دقائق.`);
    window.open(`https://wa.me/${digits}?text=${text}`, "_blank");
  };
  const openMailto = (email, name) => {
    const subject = encodeURIComponent(`بيانات الدخول لحسابك على محاسبتي — ${name}`);
    const body = encodeURIComponent(`مرحباً ${name}،\n\nتم تفعيل حسابك على محاسبتي.\n\nالإيميل: ${email}\nكلمة المرور المؤقتة: <اكتبها هنا>\n\nيُرجى تغيير كلمة المرور بعد أول دخول.\n\nمع أطيب التحيات،\nإدارة محاسبتي`);
    window.open(`mailto:${email}?subject=${subject}&body=${body}`);
  };

  const counts = items.reduce((a, r) => ({ ...a, [r.status]: (a[r.status]||0)+1 }), {});

  return (
    <div className="space-y-6" data-testid="account-requests-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2">
            <UserPlus className="w-6 h-6 text-primary" /> طلبات الحسابات الجديدة
          </h1>
          <p className="text-sm text-muted-foreground mt-1">راجع الطلبات الواردة، تواصل مع المتقدمين، ثم اعتمد أو ارفض</p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs">التصفية</Label>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44 h-9" data-testid="filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">قيد الانتظار ({counts.pending || 0})</SelectItem>
              <SelectItem value="contacted">تم التواصل</SelectItem>
              <SelectItem value="approved">معتمد</SelectItem>
              <SelectItem value="rejected">مرفوض</SelectItem>
              <SelectItem value="all">الكل</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="text-start">التاريخ</TableHead>
            <TableHead className="text-start">الاسم</TableHead>
            <TableHead className="text-start">الإيميل</TableHead>
            <TableHead className="text-start">الهاتف</TableHead>
            <TableHead className="text-start">الشركة / البلد</TableHead>
            <TableHead className="text-start">ملاحظات</TableHead>
            <TableHead className="text-start">الحالة</TableHead>
            <TableHead className="text-start">إجراء</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">لا توجد طلبات {filter !== "all" ? "بهذه الحالة" : ""}</TableCell></TableRow>}
            {items.map(r => {
              const meta = STATUS_META[r.status] || STATUS_META.pending;
              return (
                <TableRow key={r.id} data-testid={`req-row-${r.id}`}>
                  <TableCell className="text-xs">{new Date(r.created_at).toLocaleString("ar-EG")}</TableCell>
                  <TableCell className="font-semibold">{r.name}</TableCell>
                  <TableCell className="text-xs font-mono">{r.email}</TableCell>
                  <TableCell className="text-xs">{r.phone || "—"}</TableCell>
                  <TableCell className="text-xs">
                    {r.company || "—"}
                    {r.country && <div className="text-muted-foreground">{r.country}</div>}
                  </TableCell>
                  <TableCell className="text-xs max-w-[220px] truncate" title={r.notes}>{r.notes || "—"}</TableCell>
                  <TableCell><span className={`text-xs px-2 py-0.5 rounded border ${meta.color}`}>{meta.label}</span></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {r.phone && <Button size="icon" variant="ghost" onClick={()=>openWA(r.phone, r.name)} title="واتساب" className="w-7 h-7 text-emerald-600" data-testid={`wa-${r.id}`}><MessageCircle className="w-3.5 h-3.5" /></Button>}
                      <Button size="icon" variant="ghost" onClick={()=>openMailto(r.email, r.name)} title="إرسال إيميل" className="w-7 h-7 text-blue-600" data-testid={`mail-${r.id}`}><MailPlus className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="ghost" onClick={()=>openAction(r, "contacted")} title="حدد تم التواصل" className="w-7 h-7" data-testid={`contact-${r.id}`}><Phone className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="ghost" onClick={()=>openAction(r, "approved")} title="اعتماد" className="w-7 h-7 text-emerald-700" data-testid={`approve-${r.id}`}><CheckCircle2 className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="ghost" onClick={async () => {
                        if (!confirm(`تفعيل الحساب لـ ${r.name} وإرسال بيانات الدخول تلقائياً؟`)) return;
                        try {
                          const { data } = await api.post(`/account-requests/${r.id}/approve-and-create`);
                          toast.success(data.message + ` — كلمة المرور المؤقتة: ${data.temp_password}`, { duration: 15000 });
                          load();
                        } catch (e) { toast.error(formatErr(e)); }
                      }} title="⚡ تفعيل + إرسال البيانات" className="w-7 h-7 text-primary bg-primary/10" data-testid={`quick-approve-${r.id}`}><UserPlus className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="ghost" onClick={()=>openAction(r, "rejected")} title="رفض" className="w-7 h-7 text-destructive" data-testid={`reject-${r.id}`}><XCircle className="w-3.5 h-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent data-testid="action-dialog">
          <DialogHeader>
            <DialogTitle>تحديث حالة الطلب — {noteReq?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>الحالة الجديدة</Label>
              <Select value={nextStatus} onValueChange={setNextStatus}>
                <SelectTrigger data-testid="new-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="contacted">تم التواصل</SelectItem>
                  <SelectItem value="approved">معتمد</SelectItem>
                  <SelectItem value="rejected">مرفوض</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>ملاحظة داخلية (اختياري)</Label>
              <textarea value={note} onChange={(e)=>setNote(e.target.value)} rows={3} maxLength={500} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none mt-1" placeholder="مثال: أرسلت له بيانات الدخول، سيبدأ الاستخدام الأسبوع القادم" data-testid="note-input" />
            </div>
            {nextStatus === "approved" && (
              <div className="text-xs bg-emerald-50 text-emerald-800 p-2 rounded border border-emerald-200">
                💡 تذكير: بعد الاعتماد، أنشئ الحساب الفعلي من صفحة «المديرون الفرعيون» أو «المستخدمون» وأرسل البيانات إلى إيميل المتقدم.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setNoteOpen(false)}>إلغاء</Button>
            <Button onClick={doAction} data-testid="save-action-btn">تحديث</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
